<template>
  <div class="container-fluid">
    <div class="row mb-5">
      <h1>{{ transfer.seq_number ?? transfer.code ?? "-" }}</h1>
    </div>

    <div class="row mb-5">
      <div class="col-12">
        <dl class="row" v-if="transfer.created_at">
          <dt class="col-sm-4">{{ $t("COMMON.CREATED_AT") }}</dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(transfer.created_at) }}
          </dd>
        </dl>

        <dl class="row" v-if="transfer.updated_at">
          <dt class="col-sm-4">{{ $t("COMMON.UPDATED_AT") }}</dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(transfer.updated_at) }}
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("EQUIPMENT_TRANSFERS.TRANSFER_TYPE") }}</dt>
          <dd class="col-sm-8">
            {{
              transfer.transfer_type
                ? $t(`EQUIPMENT_TRANSFERS.TYPE_${transfer.transfer_type}`)
                : "-"
            }}
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("EQUIPMENT_TRANSFERS.TRANSFER_DATE") }}</dt>
          <dd class="col-sm-8">
            {{ transfer.transfer_date ? $formatDate(transfer.transfer_date, "LL") : "-" }}
          </dd>
        </dl>

        <dl class="row" v-if="transfer.transfer_type === 'TEMPORARY'">
          <dt class="col-sm-4">{{ $t("EQUIPMENT_TRANSFERS.RETURN_DATE") }}</dt>
          <dd class="col-sm-8">
            <span :class="{ 'text-danger': transfer.is_overdue }">
              {{ transfer.return_date ? $formatDate(transfer.return_date, "LL") : "-" }}
            </span>
            <badge v-if="transfer.is_overdue" type="danger" class="ml-2">
              {{ $t("EQUIPMENT_TRANSFERS.OVERDUE_RETURN") }}
            </badge>
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">{{ $t("COMMON.STATUS") }}</dt>
          <dd class="col-sm-8">
            <equipment-transfer-status-badge
              v-if="transfer.status"
              :transfer="transfer"
            />
            <span v-else>-</span>
          </dd>
        </dl>
        <dl class="row" v-if="transfer.submitted_at || transfer.submittedBy">
          <dt class="col-sm-4">{{ $t("EQUIPMENT_TRANSFERS.SUBMITTED_AT") }}</dt>
          <dd class="col-sm-8">
            {{ transfer.submitted_at ? $timeZoneDateFormat(transfer.submitted_at) : "-" }}
          </dd>
        </dl>
        <dl class="row" v-if="transfer.submittedBy">
          <dt class="col-sm-4">{{ $t("EQUIPMENT_TRANSFERS.SUBMITTED_BY") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="transfer.submittedBy" />
          </dd>
        </dl>
        <dl class="row" v-if="transfer.accepted_at || transfer.acceptedBy">
          <dt class="col-sm-4">{{ $t("EQUIPMENT_TRANSFERS.ACCEPTED_AT") }}</dt>
          <dd class="col-sm-8">
            {{ transfer.accepted_at ? $timeZoneDateFormat(transfer.accepted_at) : "-" }}
          </dd>
        </dl>
        <dl class="row" v-if="transfer.acceptedBy">
          <dt class="col-sm-4">{{ $t("EQUIPMENT_TRANSFERS.ACCEPTED_BY") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="transfer.acceptedBy" />
          </dd>
        </dl>
        <dl class="row" v-if="transfer.sent_at">
          <dt class="col-sm-4">{{ $t("EQUIPMENT_TRANSFERS.MAIL_SENT_AT") }}</dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(transfer.sent_at) }}
            <div class="text-muted small" v-if="sentRecipients">
              {{ sentRecipients }}
            </div>
          </dd>
        </dl>
        <dl class="row" v-if="transfer.sentBy">
          <dt class="col-sm-4">{{ $t("EQUIPMENT_TRANSFERS.MAIL_SENT_BY") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="transfer.sentBy" />
          </dd>
        </dl>
        <dl class="row" v-if="transfer.returned_at">
          <dt class="col-sm-4">{{ $t("EQUIPMENT_TRANSFERS.RETURNED_AT") }}</dt>
          <dd class="col-sm-8">
            {{ $timeZoneDateFormat(transfer.returned_at) }}
          </dd>
        </dl>

        <hr class="my-4" />

        <dl class="row">
          <dt class="col-sm-4">
            <h2>{{ $t("COMMON.SOURCE") }}</h2>
          </dt>
          <dd class="col-sm-8">
            <object-link
              v-if="transfer.sourceEstablishment"
              :object="transfer.sourceEstablishment"
            />
            <span v-else>
              {{ transfer.source_establishment_id || "-" }}
            </span>
          </dd>
        </dl>

        <dl class="row">
          <dt class="col-sm-4">
            <h2>{{ $t("COMMON.DESTINATION") }}</h2>
          </dt>
          <dd class="col-sm-8">
            <object-link
              v-if="transfer.destinationEstablishment"
              :object="transfer.destinationEstablishment"
            />
            <span v-else>
              {{ transfer.destination_establishment_id || "-" }}
            </span>
          </dd>
        </dl>

        <hr class="my-4" />

        <dl class="row" v-if="transfer.organization">
          <dt class="col-sm-4">{{ $t("COMMON.ORGANIZATION") }}</dt>
          <dd class="col-sm-8">
            <organization :organization="transfer.organization" />
          </dd>
        </dl>

        <dl
          class="row"
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_SERVICECENTERS) && transfer.serviceCenter"
        >
          <dt class="col-sm-4">{{ $t("COMMON.SERVICECENTER") }}</dt>
          <dd class="col-sm-8">
            <object-link :object="transfer.serviceCenter" />
          </dd>
        </dl>

        <dl class="row" v-if="transfer.excerpt">
          <dt class="col-sm-4">{{ $t("COMMON.NOTE") }}</dt>
          <dd class="col-sm-8">
            <div v-html="transfer.excerpt"></div>
          </dd>
        </dl>
      </div>
    </div>
  </div>
</template>

<script>
import EquipmentTransferStatusBadge from "./EquipmentTransferStatusBadge.vue";

export default {
  name: "equipment-transfer-view-global",
  components: { EquipmentTransferStatusBadge },
  props: {
    transfer: {
      type: Object,
      required: true,
    },
  },
  computed: {
    sentRecipients() {
      const sentTo = this.transfer.sent_to || {};
      return [...(sentTo.to || []), ...(sentTo.cc || []), ...(sentTo.bcc || [])].join(
        ", "
      );
    },
  },
};
</script>
